import React, { useEffect, useState } from "react";
import { Card, Label, Input, FormGroup, Table } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../../Components/Common/Spinner";
import AppModal from "../../../../Components/Common/AppModal";
import TelephonyOnboardingWizard from "../../../../Components/popUpComponents/TelephonyOnboarding/TelephonyOnboardingWizard";
import {
  VOICE_OPTIONS,
  MODEL_OPTIONS,
  SAMPLE_PROMPTS,
} from "./receptionistOptions";
import {
  thirdPartyVapiList,
  thirdPartyVapiCreate,
  thirdPartyVapiCreateSuccess,
  thirdPartyVapiUpdate,
  thirdPartyVapiUpdateSuccess,
  thirdPartyVapiDelete,
  thirdPartyVapiDeleteSuccess,
  thirdPartyVapiProvision,
  thirdPartyVapiProvisionSuccess,
  thirdPartyVapiLinkTwilio,
  thirdPartyVapiLinkTwilioSuccess,
  thirdPartyVapiToggle,
  thirdPartyVapiToggleSuccess,
} from "../../../../store/thirdPartyIntegration/action";

// Virtual Receptionist is per-company and now MULTIPLE: a company can run several
// independent AI receptionists, each with its own answering prompt/greeting, a
// unique assistant + SIP account, and its own Twilio number. This panel lists
// them and drives create/edit/provision/link/enable/delete. The onboarding
// wizard offers a guided create-provision-link flow for a brand new one.
const emptyForm = {
  Id: null,
  name: "",
  system_prompt: "",
  greeting: "",
  voice: "",
  model: "",
  enabled: true,
  assistant_id: "",
  sip_uri: "",
  twilio_number: "",
  has_assistant: 0,
  twilio_linked: 0,
};

export default function VapiIntegration() {
  const dispatch = useDispatch();

  // Editor modal state. `form` holds the receptionist being created/edited.
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const isEditing = !!form.Id;

  // Telephony onboarding wizard — both launch points live here alongside the
  // dedicated Phone Numbers screen. "Order a number" upsells the receptionist;
  // "Guided setup" runs the receptionist-first flow (which then buys a number).
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState("number");

  const openWizard = (mode) => {
    setWizardMode(mode);
    setWizardOpen(true);
  };

  const {
    listDataAPI,
    listLoading,
    createDataAPI,
    createLoading,
    updateDataAPI,
    updateLoading,
    deleteDataAPI,
    provisionDataAPI,
    linkTwilioDataAPI,
    linkTwilioLoading,
    toggleDataAPI,
  } = useSelector((state) => ({
    listDataAPI: state.ThirdPartyIntegration.thirdPartyVapiListData,
    listLoading: state.ThirdPartyIntegration.thirdPartyVapiListLoading,
    createDataAPI: state.ThirdPartyIntegration.thirdPartyVapiCreateData,
    createLoading: state.ThirdPartyIntegration.thirdPartyVapiCreateLoading,
    updateDataAPI: state.ThirdPartyIntegration.thirdPartyVapiUpdateData,
    updateLoading: state.ThirdPartyIntegration.thirdPartyVapiUpdateLoading,
    deleteDataAPI: state.ThirdPartyIntegration.thirdPartyVapiDeleteData,
    provisionDataAPI: state.ThirdPartyIntegration.thirdPartyVapiProvisionData,
    linkTwilioDataAPI: state.ThirdPartyIntegration.thirdPartyVapiLinkTwilioData,
    linkTwilioLoading:
      state.ThirdPartyIntegration.thirdPartyVapiLinkTwilioLoading,
    toggleDataAPI: state.ThirdPartyIntegration.thirdPartyVapiToggleData,
  }));

  const receptionists = listDataAPI?.receptionist_list || [];

  useEffect(() => {
    dispatch(thirdPartyVapiList());
  }, []);

  // ---- Create result ----
  useEffect(() => {
    if (isEmpty(createDataAPI)) return;
    if (createDataAPI.New_VirtualReceptionist_Id || createDataAPI.status === 200) {
      toast.success(createDataAPI?.message || "Receptionist created");
      setEditorOpen(false);
      dispatch(thirdPartyVapiCreateSuccess([]));
      dispatch(thirdPartyVapiList());
    } else {
      toast.error(createDataAPI?.error || createDataAPI?.message || "Create failed");
      dispatch(thirdPartyVapiCreateSuccess([]));
    }
  }, [createDataAPI]);

  // ---- Update result ----
  useEffect(() => {
    if (isEmpty(updateDataAPI)) return;
    if (updateDataAPI.status === 200) {
      toast.success(updateDataAPI?.message || "Receptionist saved");
      setEditorOpen(false);
      dispatch(thirdPartyVapiUpdateSuccess([]));
      dispatch(thirdPartyVapiList());
    } else {
      toast.error(updateDataAPI?.error || updateDataAPI?.message || "Save failed");
      dispatch(thirdPartyVapiUpdateSuccess([]));
    }
  }, [updateDataAPI]);

  // ---- Delete result ----
  useEffect(() => {
    if (isEmpty(deleteDataAPI)) return;
    if (deleteDataAPI.status === 200) {
      toast.success(deleteDataAPI?.message || "Receptionist deleted");
      dispatch(thirdPartyVapiDeleteSuccess([]));
      dispatch(thirdPartyVapiList());
    } else {
      toast.error(deleteDataAPI?.error || deleteDataAPI?.message || "Delete failed");
      dispatch(thirdPartyVapiDeleteSuccess([]));
    }
  }, [deleteDataAPI]);

  // ---- Provision result ----
  useEffect(() => {
    if (isEmpty(provisionDataAPI)) return;
    if (provisionDataAPI.assistant_id || provisionDataAPI.status === 200) {
      toast.success(provisionDataAPI?.message || "Assistant provisioned");
      dispatch(thirdPartyVapiProvisionSuccess([]));
      dispatch(thirdPartyVapiList());
    } else {
      toast.error(
        provisionDataAPI?.error || provisionDataAPI?.message || "Provision failed"
      );
      dispatch(thirdPartyVapiProvisionSuccess([]));
    }
  }, [provisionDataAPI]);

  // ---- Link number result ----
  useEffect(() => {
    if (isEmpty(linkTwilioDataAPI)) return;
    if (linkTwilioDataAPI.twilio_linked || linkTwilioDataAPI.status === 200) {
      toast.success(linkTwilioDataAPI?.message || "Number linked");
      setEditorOpen(false);
      dispatch(thirdPartyVapiLinkTwilioSuccess([]));
      dispatch(thirdPartyVapiList());
    } else {
      toast.error(
        linkTwilioDataAPI?.error || linkTwilioDataAPI?.message || "Link failed"
      );
      dispatch(thirdPartyVapiLinkTwilioSuccess([]));
    }
  }, [linkTwilioDataAPI]);

  // ---- Toggle result ----
  useEffect(() => {
    if (isEmpty(toggleDataAPI)) return;
    if (toggleDataAPI.enabled !== undefined || toggleDataAPI.status === 200) {
      toast.success(toggleDataAPI?.message || "Updated");
      dispatch(thirdPartyVapiToggleSuccess([]));
      dispatch(thirdPartyVapiList());
    } else {
      toast.error(toggleDataAPI?.error || toggleDataAPI?.message || "Update failed");
      dispatch(thirdPartyVapiToggleSuccess([]));
    }
  }, [toggleDataAPI]);

  // ---- Editor helpers ----
  const openNew = () => {
    setForm(emptyForm);
    setEditorOpen(true);
  };

  const openEdit = (r) => {
    setForm({
      Id: r.id,
      name: r.name || "",
      system_prompt: r.system_prompt || "",
      greeting: r.greeting || "",
      voice: r.voice || "",
      model: r.model || "",
      enabled: r.enabled === 1,
      assistant_id: r.assistant_id || "",
      sip_uri: r.sip_uri || "",
      twilio_number: r.twilio_number || "",
      has_assistant: r.has_assistant || 0,
      twilio_linked: r.twilio_linked || 0,
    });
    setEditorOpen(true);
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = () => {
    if (!form.name.trim()) {
      toast.error("Give the receptionist a name");
      return;
    }
    if (!form.system_prompt.trim()) {
      toast.error("Enter the AI answering prompt");
      return;
    }
    const body = {
      name: form.name.trim(),
      system_prompt: form.system_prompt,
      greeting: form.greeting,
      voice: form.voice,
      model: form.model,
      enabled: form.enabled ? 1 : 0,
    };
    if (isEditing) {
      dispatch(thirdPartyVapiUpdate([{ Id: form.Id, ...body }]));
    } else {
      dispatch(thirdPartyVapiCreate([body]));
    }
  };

  const handleProvision = (id) => {
    dispatch(thirdPartyVapiProvision([{ Id: id }]));
  };

  const handleLinkNumber = () => {
    if (!form.twilio_number.trim()) {
      toast.error("Enter the Twilio number in E.164 format");
      return;
    }
    dispatch(
      thirdPartyVapiLinkTwilio([
        { Id: form.Id, twilio_number: form.twilio_number.trim() },
      ])
    );
  };

  const handleToggle = (r) => {
    dispatch(thirdPartyVapiToggle([{ Id: r.id, enabled: r.enabled === 1 ? 0 : 1 }]));
  };

  const handleDelete = (r) => {
    if (
      !window.confirm(
        `Delete "${r.name || "this receptionist"}"? This cannot be undone.`
      )
    )
      return;
    dispatch(thirdPartyVapiDelete([{ Id: r.id }]));
  };

  const saving = createLoading || updateLoading;

  return (
    <Card className="p-3">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h5 className="mb-0">Virtual Receptionist</h5>
        <button className="btn btn-primary" onClick={openNew}>
          <i className="ri-add-line me-1"></i> Add receptionist
        </button>
      </div>

      {/* Cross-sell launch points: order a number (upsells the receptionist) or
          run the guided receptionist-first setup (which then buys a number). */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <button
          className="btn btn-outline-primary"
          onClick={() => openWizard("number")}
        >
          <i className="ri-phone-line me-1"></i> Order a number
        </button>
        <button
          className="btn btn-outline-primary"
          onClick={() => openWizard("receptionist")}
        >
          <i className="ri-magic-line me-1"></i> Guided setup
        </button>
      </div>

      {listLoading ? (
        <LoadingSpinner />
      ) : receptionists.length > 0 ? (
        <Table responsive className="align-middle mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Number</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {receptionists.map((r) => (
              <tr key={r.id}>
                <td className="fw-bold">{r.name || "Untitled"}</td>
                <td>{r.twilio_number || "—"}</td>
                <td>
                  <div className="d-flex gap-1 flex-wrap">
                    {r.enabled === 1 ? (
                      <span className="badge bg-success-subtle text-success">
                        Enabled
                      </span>
                    ) : (
                      <span className="badge bg-secondary-subtle text-secondary">
                        Disabled
                      </span>
                    )}
                    {r.has_assistant === 1 && (
                      <span className="badge bg-info-subtle text-info">
                        Provisioned
                      </span>
                    )}
                    {r.twilio_linked === 1 && (
                      <span className="badge bg-primary-subtle text-primary">
                        Linked
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-end">
                  <div className="d-flex gap-1 justify-content-end flex-wrap">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => openEdit(r)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() => handleProvision(r.id)}
                    >
                      {r.has_assistant === 1 ? "Re-provision" : "Provision"}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => openEdit(r)}
                    >
                      Link number
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleToggle(r)}
                    >
                      {r.enabled === 1 ? "Disable" : "Enable"}
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(r)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div className="text-center text-muted py-5">
          <i className="ri-robot-2-line fs-1 d-block mb-2 opacity-50"></i>
          <p className="mb-3">You don&apos;t have any receptionists yet.</p>
          <button className="btn btn-primary" onClick={openNew}>
            <i className="ri-add-line me-1"></i> Add your first receptionist
          </button>
        </div>
      )}

      {/* ---- Editor modal ---- */}
      <AppModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={isEditing ? "Edit receptionist" : "Add receptionist"}
        subtitle="Configure how the AI answers inbound calls."
        icon={<i className="ri-robot-2-line" />}
        headerAccent="indigo"
        size="lg"
        loading={saving}
        onSubmit={handleSave}
        submitLabel={isEditing ? "Save changes" : "Create receptionist"}
      >
        <FormGroup>
          <Label for="rcpt-name">Name</Label>
          <Input
            id="rcpt-name"
            type="text"
            value={form.name}
            placeholder="e.g. Main line, Out of hours"
            onChange={(e) => setField("name", e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <div className="d-flex justify-content-between align-items-center">
            <Label for="rcpt-system-prompt" className="mb-0">
              AI Answering Prompt
            </Label>
            <Input
              type="select"
              bsSize="sm"
              style={{ width: "auto" }}
              value=""
              onChange={(e) => {
                const s = SAMPLE_PROMPTS[Number(e.target.value)];
                if (!s) return;
                setForm((f) => ({
                  ...f,
                  system_prompt: s.prompt,
                  greeting: s.greeting || f.greeting,
                }));
              }}
            >
              <option value="">Insert a sample prompt…</option>
              {SAMPLE_PROMPTS.map((s, i) => (
                <option key={s.label} value={i}>
                  {s.label}
                </option>
              ))}
            </Input>
          </div>
          <Input
            id="rcpt-system-prompt"
            type="textarea"
            rows={8}
            className="mt-1"
            value={form.system_prompt}
            placeholder="You are the friendly virtual receptionist for..."
            onChange={(e) => setField("system_prompt", e.target.value)}
          />
          <small className="text-muted">
            How the AI should greet and handle callers — pick a sample to start,
            then tweak it
          </small>
        </FormGroup>

        <FormGroup>
          <Label for="rcpt-greeting">Greeting</Label>
          <Input
            id="rcpt-greeting"
            type="text"
            value={form.greeting}
            placeholder="Hello, thanks for calling - how can I help?"
            onChange={(e) => setField("greeting", e.target.value)}
          />
          <small className="text-muted">The first thing the AI says</small>
        </FormGroup>

        <div className="row">
          <div className="col-md-6">
            <FormGroup>
              <Label for="rcpt-voice">Voice</Label>
              <Input
                id="rcpt-voice"
                type="select"
                value={form.voice || ""}
                onChange={(e) => setField("voice", e.target.value)}
              >
                <option value="">Default voice</option>
                {VOICE_OPTIONS.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </div>
          <div className="col-md-6">
            <FormGroup>
              <Label for="rcpt-model">Model</Label>
              <Input
                id="rcpt-model"
                type="select"
                value={form.model || ""}
                onChange={(e) => setField("model", e.target.value)}
              >
                <option value="">Default model</option>
                {MODEL_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </div>
        </div>

        <FormGroup switch className="d-flex align-items-center gap-2 my-2">
          <Input
            type="switch"
            role="switch"
            checked={form.enabled}
            onChange={(e) => setField("enabled", e.target.checked)}
          />
          <Label check className="mb-0">
            Receptionist {form.enabled ? "enabled" : "disabled"}
          </Label>
        </FormGroup>

        {/* Existing receptionist: assistant / SIP readouts + number linking. */}
        {isEditing && (
          <>
            <hr className="my-3" />
            <h6>Assistant &amp; SIP account</h6>
            {form.has_assistant === 1 ? (
              <>
                <FormGroup>
                  <Label>Assistant ID</Label>
                  <Input type="text" value={form.assistant_id} readOnly />
                </FormGroup>
                <FormGroup>
                  <Label>SIP account</Label>
                  <Input type="text" value={form.sip_uri} readOnly />
                  <small className="text-muted">Your unique SIP account</small>
                </FormGroup>
              </>
            ) : (
              <p className="text-muted">
                Not provisioned yet — use Provision on the list to create the
                assistant and SIP account.
              </p>
            )}

            <hr className="my-3" />
            <h6>Answering number</h6>
            <FormGroup>
              <Label for="rcpt-twilio-number">Twilio number (E.164)</Label>
              <Input
                id="rcpt-twilio-number"
                type="text"
                value={form.twilio_number}
                placeholder="+441275..."
                onChange={(e) => setField("twilio_number", e.target.value)}
              />
            </FormGroup>
            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleLinkNumber}
                disabled={linkTwilioLoading}
              >
                Link number
              </button>
              {form.twilio_linked === 1 ? (
                <span className="badge bg-success">Linked &#10003;</span>
              ) : (
                <span className="badge bg-secondary">Not linked</span>
              )}
            </div>
          </>
        )}
      </AppModal>

      <TelephonyOnboardingWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        entryMode={wizardMode}
        onComplete={() => dispatch(thirdPartyVapiList())}
      />
    </Card>
  );
}
